import React, { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createTrialAccount } from '../../services/authService';
import LoadingSpinner from '../common/LoadingSpinner';
import CepField from '../common/CepField';

const TrialRegistration = () => {
  const [formData, setFormData] = useState({
    // Dados da empresa
    name: '',
    email: '',
    phone: '',
    cpfCnpj: '',
    
    // Dados de endereço (opcionais)
    cep: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    
    // Dados de acesso
    password: '',
    confirmPassword: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCepChange = useCallback((cepValue) => {
    setFormData(prev => ({ ...prev, cep: cepValue }));
  }, []);

  const handleAddressChange = useCallback((addressData) => {
    // Preencher automaticamente os campos de endereço com os dados do CEP
    setFormData(prev => ({
      ...prev,
      street: addressData.endereco || prev.street,
      neighborhood: addressData.bairro || prev.neighborhood,
      city: addressData.cidade || prev.city,
      state: addressData.estado || prev.state
    }));
  }, []);

  const validateForm = () => {
    if (!formData.name) {
      setError('Nome da empresa é obrigatório');
      return false;
    }
    
    if (!formData.email) {
      setError('Email é obrigatório');
      return false;
    }
    
    if (!formData.phone) {
      setError('Telefone é obrigatório');
      return false;
    }
    
    if (!formData.cpfCnpj) {
      setError('CPF/CNPJ é obrigatório');
      return false;
    }
    
    if (!formData.password) {
      setError('Senha é obrigatória');
      return false;
    }
    
    if (formData.password.length < 6) {
      setError('Senha deve ter pelo menos 6 caracteres');
      return false;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError('Senhas não coincidem');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const result = await createTrialAccount(formData.email, formData.password, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        cpfCnpj: formData.cpfCnpj,
        address: {
          cep: formData.cep,
          street: formData.street,
          number: formData.number,
          complement: formData.complement,
          neighborhood: formData.neighborhood,
          city: formData.city,
          state: formData.state
        }
      });
      
      // Se chegou até aqui, o cadastro foi bem-sucedido
      console.log('Trial account created successfully:', result);
      
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      
    } catch (err) {
      console.error('Trial registration error:', err);
      
      // Tratar diferentes tipos de erro com mensagens mais específicas
      let errorMessage = err.message;
      
      if (err.message.includes('email já está em uso')) {
        errorMessage = 'Este email já está cadastrado. Se você teve problemas no cadastro anterior, aguarde alguns minutos e tente novamente, ou entre em contato conosco.';
      } else if (err.message.includes('weak-password')) {
        errorMessage = 'A senha deve ter pelo menos 6 caracteres.';
      } else if (err.message.includes('invalid-email')) {
        errorMessage = 'Email inválido. Verifique o formato do email.';
      } else if (err.message.includes('network-request-failed')) {
        errorMessage = 'Erro de conexão. Verifique sua internet e tente novamente.';
      } else if (err.message.includes('Firestore')) {
        errorMessage = 'Erro ao salvar dados. Tente novamente em alguns instantes.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <LoadingSpinner size="lg" />
          <h3 className="text-lg font-semibold text-gray-900 mt-4 mb-2">
            Criando sua conta...
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Estamos configurando seu período de teste gratuito. Isso pode levar alguns segundos.
          </p>
          <div className="text-xs text-gray-500">
            Por favor, não feche esta página.
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Cadastro Realizado com Sucesso!
          </h2>
          <p className="text-gray-600 mb-4">
            Sua conta de teste foi criada com sucesso. Você terá <strong>5 dias</strong> para testar todas as funcionalidades da plataforma.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-blue-700">
              <strong>Próximos passos:</strong><br/>
              1. Faça login com seu email e senha<br/>
              2. Configure sua empresa<br/>
              3. Comece a cadastrar clientes e pontos
            </p>
          </div>
          <p className="text-sm text-gray-500">
            Redirecionando para o login em alguns segundos...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto h-12 w-12 rounded-lg overflow-hidden border border-gray-200 bg-white flex items-center justify-center">
            <img 
              src="/logo1200.png" 
              alt="MeuFiel Logo" 
              className="h-full w-full object-contain"
            />
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Teste Grátis por 5 Dias
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Cadastre sua empresa e teste todas as funcionalidades
          </p>
        </div>

        {/* Formulário */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {/* Dados da Empresa */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Dados da Empresa
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome da Empresa *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Digite o nome da empresa"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="email@empresa.com"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telefone *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="(11) 99999-9999"
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CPF/CNPJ *
                  </label>
                  <input
                    type="text"
                    name="cpfCnpj"
                    value={formData.cpfCnpj}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="000.000.000-00 ou 00.000.000/0001-00"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Endereço (Opcional) */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Endereço <span className="text-sm font-normal text-gray-500">(Opcional)</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CEP
                  </label>
                  <CepField
                    value={formData.cep}
                    onChange={handleCepChange}
                    onAddressChange={handleAddressChange}
                    placeholder="00000-000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Número
                  </label>
                  <input
                    type="text"
                    name="number"
                    value={formData.number}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="123"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rua/Logradouro
                </label>
                <input
                  type="text"
                  name="street"
                  value={formData.street}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nome da rua, avenida, etc."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bairro
                  </label>
                  <input
                    type="text"
                    name="neighborhood"
                    value={formData.neighborhood}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Bairro"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cidade
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nome da cidade"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estado
                  </label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="UF"
                    maxLength="2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Complemento
                  </label>
                  <input
                    type="text"
                    name="complement"
                    value={formData.complement}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Apartamento, sala, etc."
                  />
                </div>
              </div>
            </div>

            {/* Dados de Acesso */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Dados de Acesso
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Senha *
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Mínimo 6 caracteres"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirmar Senha *
                  </label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Digite a senha novamente"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Botões */}
            <div className="flex flex-col space-y-4 pt-6">
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Criando conta...' : 'Criar Conta de Teste'}
              </button>

              <Link
                to="/login"
                className="w-full border border-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-50 transition-colors font-medium text-center"
              >
                Já tenho uma conta
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TrialRegistration;
